import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  IconButton,
  Tooltip,
  Checkbox,
  Icon,
} from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';

const propTypes = {
  // Data props
  holdingsData: PropTypes.array.isRequired,
  selectedHoldings: PropTypes.array.isRequired,
  totalUndoItems: PropTypes.number.isRequired,
  isBatchModeActive: PropTypes.bool,

  // Action handlers
  onSelectAllHoldings: PropTypes.func.isRequired,
  onClearSelection: PropTypes.func.isRequired,
  onBatchCreate: PropTypes.func.isRequired,
  onShowBatchCreate: PropTypes.func.isRequired,
  onBatchEdit: PropTypes.func.isRequired,
  onBatchDelete: PropTypes.func.isRequired,
  onShowUndoHistory: PropTypes.func.isRequired,
};

function HoldingBatchToolbar({
  // Data props
  holdingsData,
  selectedHoldings,
  totalUndoItems,
  isBatchModeActive = false,

  // Action handlers
  onSelectAllHoldings,
  onClearSelection,
  onBatchCreate,
  onShowBatchCreate,
  onBatchEdit,
  onBatchDelete,
  onShowUndoHistory,
}) {
  const selectedCount = selectedHoldings.length;
  const allSelected =
    selectedCount === holdingsData.length && holdingsData.length > 0;
  const someSelected = selectedCount > 0 && selectedCount < holdingsData.length;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        marginBottom: '16px',
        backgroundColor: selectedCount > 0 ? '#e3f2fd' : '#f8f9fa',
        border: `1px solid ${selectedCount > 0 ? '#1976d2' : '#e9ecef'}`,
        borderRadius: '6px',
        transition: 'all 0.2s ease',
      }}
    >
      {/* Left side - Batch operations */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        {holdingsData.length > 0 && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Tooltip
                text={
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.holdingBatchToolbar.selectAll.tooltip"
                    defaultMessage="Select all holdings"
                  />
                }
              >
                {({ ref, ariaIds }) => (
                  <div
                    ref={ref}
                    aria-labelledby={ariaIds.text}
                    style={{
                      display: 'inline-block',
                      cursor: 'pointer',
                    }}
                  >
                    <Checkbox
                      checked={allSelected}
                      onChange={() => {
                        if (allSelected) {
                          onClearSelection();
                        } else {
                          onSelectAllHoldings(holdingsData);
                        }
                      }}
                      indeterminate={someSelected}
                      disabled={holdingsData.length === 0}
                    />
                  </div>
                )}
              </Tooltip>
              <span style={{ fontSize: '14px', color: '#666' }}>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.holdingBatchToolbar.selectionStatus"
                  values={{
                    selected: selectedCount,
                    total: holdingsData.length,
                  }}
                  defaultMessage="{selected} of {total} selected"
                />
              </span>
            </div>
          </>
        )}
      </div>

      {/* Right side - Action buttons */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        {selectedCount > 0 ? (
          // Batch operation buttons
          <>
            <Tooltip
              key="batch-edit-tooltip"
              id="holding-batch-toolbar-batch-edit-tooltip"
              text={
                <FormattedMessage
                  id="ui-plugin-inventory-templates.holdingBatchToolbar.batchEdit.tooltip"
                  defaultMessage="Edit selected holdings"
                />
              }
            >
              {({ ref, ariaIds }) => (
                <IconButton
                  key="batch-edit-button"
                  icon="edit"
                  onClick={onBatchEdit}
                  aria-labelledby={ariaIds.text}
                  ref={ref}
                />
              )}
            </Tooltip>

            <Tooltip
              key="batch-delete-tooltip"
              id="holding-batch-toolbar-batch-delete-tooltip"
              text={
                <FormattedMessage
                  id="ui-plugin-inventory-templates.holdingBatchToolbar.batchDelete.tooltip"
                  defaultMessage="Delete selected holdings"
                />
              }
            >
              {({ ref, ariaIds }) => (
                <IconButton
                  key="batch-delete-button"
                  icon="trash"
                  onClick={onBatchDelete}
                  aria-labelledby={ariaIds.text}
                  ref={ref}
                />
              )}
            </Tooltip>
          </>
        ) : (
          <>
            {/* Create multiple holdings button */}
            <Tooltip
              key="create-multiple-tooltip"
              text={
                <FormattedMessage
                  id="ui-plugin-inventory-templates.holdingBatchToolbar.createMultipleHoldings.tooltip"
                  defaultMessage="Create multiple holdings"
                />
              }
            >
              {({ ref, ariaIds }) => (
                <Button
                  key="create-multiple-button"
                  // buttonStyle="slim"
                  onClick={onShowBatchCreate}
                  aria-labelledby={ariaIds.text}
                  ref={ref}
                  marginBottom0
                  disabled={isBatchModeActive}
                >
                  <Icon icon="duplicate" />
                </Button>
              )}
            </Tooltip>

            {/* Create new holding button */}
            <Tooltip
              key="create-single-tooltip"
              text={
                <FormattedMessage
                  id="ui-plugin-inventory-templates.holdingBatchToolbar.createHolding.tooltip"
                  defaultMessage="Create new holding"
                />
              }
            >
              {({ ref, ariaIds }) => (
                <Button
                  key="create-single-button"
                  buttonStyle="primary"
                  onClick={onBatchCreate}
                  aria-labelledby={ariaIds.text}
                  ref={ref}
                  marginBottom0
                  disabled={isBatchModeActive}
                >
                  <Icon icon="plus-sign" />
                </Button>
              )}
            </Tooltip>

            {/* Undo button - only show when there are undo items */}
            {totalUndoItems > 0 && (
              <Tooltip
                key="undo-tooltip"
                text={
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.holdingsVolumeManagement.undoHistory.tooltip"
                    defaultMessage="Show operations history and undo"
                  />
                }
              >
                {({ ref, ariaIds }) => (
                  <Button
                    key="undo-button"
                    buttonStyle="default slim"
                    onClick={onShowUndoHistory}
                    aria-labelledby={ariaIds.text}
                    ref={ref}
                    marginBottom0
                  >
                    ↶ ({totalUndoItems})
                  </Button>
                )}
              </Tooltip>
            )}
          </>
        )}
      </div>
    </div>
  );
}

HoldingBatchToolbar.propTypes = propTypes;

export default HoldingBatchToolbar;
